/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { serviceTypes } from '@opengeoweb/store';
import {
  getWMSServiceId,
  invalidateWMSGetCapabilities,
  queryWMSLayers,
  queryWMSServiceInfo,
} from '@opengeoweb/webmap';
import {
  fetchStacCatalogRoot,
  fetchStacCatalogCollections,
  stacCatalogCollectionToLayerProps,
} from '@opengeoweb/webmap-react';
import type { layerSelectTypes } from '../../store';

export enum DetectableServiceType {
  STAC = 'STAC',
  WMS = 'WMS',
}

export type ValidationResult = string | boolean;

const stripStacCollectionsAndTrailingSlashes = (
  serviceUrl: string | null | undefined,
): string =>
  (serviceUrl ?? '').replace(/\/collections.*$/i, '').replace(/\/+$/, '');

export const normalizeStacServiceUrl = (
  serviceUrl: string | null | undefined,
): string => {
  const normalizedUrl = stripStacCollectionsAndTrailingSlashes(serviceUrl);
  return normalizedUrl ? `${normalizedUrl}/` : '';
};

export const validateServiceName = (
  t: TFunction,
  value: string,
  services: layerSelectTypes.ActiveServiceObjectEntities,
  ownServiceName = '',
): ValidationResult => {
  const serviceName = value?.toLowerCase();
  const foundName = Object.keys(services).find(
    (serviceId) =>
      services[serviceId]?.serviceName!.toLowerCase() === serviceName &&
      serviceName !== ownServiceName.toLocaleLowerCase(),
  );
  return foundName === undefined ? true : t('validations-name-existing');
};

const isValidUrl = (url: string): boolean => {
  if (url === '' || url === null || typeof url === 'undefined') {
    return false;
  }
  const matcher = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|[0-9a-zA-Z]+[:?\d]*)\S*$/;
  if (!matcher.test(url)) {
    return false;
  }
  return true;
};

export const validateServiceUrl = (
  t: TFunction,
  value: string,
  services: layerSelectTypes.ActiveServiceObjectEntities,
): ValidationResult => {
  const serviceUrl = value;
  // validate valid url
  const isServiceUrlValid = isValidUrl(serviceUrl);
  if (!isServiceUrlValid) {
    return t('validations-service-valid-url');
  }
  // check url existing
  const normalizedServiceUrl =
    stripStacCollectionsAndTrailingSlashes(serviceUrl);
  const foundUrl = Object.keys(services).find(
    (serviceId) =>
      stripStacCollectionsAndTrailingSlashes(
        services[serviceId]?.serviceUrl,
      ) === normalizedServiceUrl,
  );
  if (foundUrl) {
    return t('validations-service-existing');
  }

  return true;
};

export const loadWMSService = async (
  serviceUrl: string,
  forceReload = false,
): Promise<serviceTypes.SetLayersForServicePayload> => {
  if (forceReload) {
    await invalidateWMSGetCapabilities(serviceUrl);
  }

  const layers = await queryWMSLayers(serviceUrl);
  const service = await queryWMSServiceInfo(serviceUrl);

  return {
    id: service.id || getWMSServiceId(serviceUrl),
    name: service.title,
    serviceUrl,
    abstract: service.abstract,
    layers,
    scope: 'user',
  };
};

export const detectServiceType = async (
  url: string,
): Promise<DetectableServiceType> => {
  const trimmedUrl = url.trim();
  const isStacServiceUrl = async (candidateUrl: string): Promise<boolean> => {
    try {
      const root = await fetchStacCatalogRoot(candidateUrl);
      const hasStacRootShape =
        root.stac_version ||
        root.type === 'Catalog' ||
        Array.isArray(root.conformsTo);

      if (hasStacRootShape) {
        return true;
      }

      return Boolean(
        root.type === 'Collection' &&
        root.stac_version &&
        Array.isArray(root.links),
      );
    } catch {
      return false;
    }
  };

  if (await isStacServiceUrl(trimmedUrl)) {
    return DetectableServiceType.STAC;
  }

  if (!trimmedUrl.endsWith('/') && (await isStacServiceUrl(`${trimmedUrl}/`))) {
    return DetectableServiceType.STAC;
  }

  return DetectableServiceType.WMS;
};

export const loadSTACService = async (
  serviceUrl: string,
): Promise<serviceTypes.SetLayersForServicePayload> => {
  const [root, collections] = await Promise.all([
    fetchStacCatalogRoot(serviceUrl),
    fetchStacCatalogCollections(serviceUrl),
  ]);

  const serviceId = serviceUrl
    .replace(/\/+$/, '')
    .replace(/[^a-zA-Z0-9]/g, '_');

  const layers: serviceTypes.ServiceLayer[] = collections.map(
    stacCatalogCollectionToLayerProps,
  );

  return {
    // Use URL-based ids so different STAC endpoints with identical root.id
    // values (e.g. "stac-fastapi") can coexist in the service store.
    id: serviceId,
    name: root.title ?? root.id ?? serviceId,
    serviceUrl,
    abstract: root.description ?? '',
    layers,
    scope: 'user',
    type: DetectableServiceType.STAC,
  };
};
